import { useMemo, useState } from "react";
import { Box } from "../../../../components/Box";
import { ExpansionPanel } from "../../../../components/ExpansionPanel";
import Select, { Option } from "../../../../components/Select";
import { CardLine } from "../../../IndividualView";
import {
  AnswerRow,
  AnswerTable,
  AnswerValue,
  DocumentLink,
  FieldValue,
  FormTitle,
  SectionCard,
  SectionTitle,
} from "../../../../components/FormAnswers";
import { formatAnswerValue } from "../../../../utils/formatters";
import { ProfileForm, ProfileFormQuestion } from "../../../../types/Forms";
import {
  deriveRecords,
  latestAnswerForRecord,
  recordHasAnswersInForm,
  recordLabel,
} from "./formsHelpers";

/**
 * By application: pick one Individual record and see its route through the
 * application, form by form, in the same question/answer table the Applications
 * Forms tab uses. Shows that record's latest answer per question and
 * "Not answered" where it left one blank.
 *
 * A Profile holds one Individual record per application, so the picker is
 * labelled by application for staff while the code keeps the record naming that
 * matches the API payload.
 */
export const FormsByRecord = ({ forms }: { forms: ProfileForm[] }) => {
  const records = useMemo(() => deriveRecords(forms), [forms]);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const activeId = selectedId ?? records[0]?.id ?? null;

  if (!records.length) {
    return <CardLine>No applications have answered these forms yet.</CardLine>;
  }

  const options: Option[] = records.map((record) => ({
    value: record.id,
    label: recordLabel(record),
  }));
  const selectedOption =
    options.find((option) => option.value === activeId) ?? null;

  const visibleForms =
    activeId == null
      ? []
      : forms.filter((form) => recordHasAnswersInForm(form, activeId));

  const renderRow = (question: ProfileFormQuestion) => {
    const answer =
      activeId == null
        ? null
        : latestAnswerForRecord(question.answers, activeId);
    const value = answer ? formatAnswerValue(answer, question.field_type) : "";
    const hasDocument = !!answer?.document;

    return (
      <AnswerRow
        key={question.id}
        title={question.title}
        fieldType={question.field_type}
      >
        {value || hasDocument ? (
          <FieldValue>
            {hasDocument ? (
              <DocumentLink href={answer!.document as string}>
                {value || "View document"}
              </DocumentLink>
            ) : (
              <AnswerValue value={value} fieldType={question.field_type} />
            )}
          </FieldValue>
        ) : (
          <FieldValue muted>Not answered</FieldValue>
        )}
      </AnswerRow>
    );
  };

  return (
    <Box>
      <Box maxWidth="460px" mb={3}>
        <Select
          label="Showing answers for"
          options={options}
          value={selectedOption}
          onChange={(option) =>
            setSelectedId(Number((option as Option).value))
          }
        />
      </Box>

      {visibleForms.length ? (
        visibleForms.map((form) => (
          <Box key={form.id} mb={3}>
            <ExpansionPanel title={<FormTitle>{form.name}</FormTitle>} defaultExpanded>
              {form.sections?.length ? (
                form.sections.map((section, sIdx) => {
                  const questions = section.questions ?? [];
                  return (
                    <SectionCard key={section.id ?? sIdx}>
                      <SectionTitle>
                        {section.title || `Section ${sIdx + 1}`}
                      </SectionTitle>
                      {questions.length ? (
                        <AnswerTable>{questions.map(renderRow)}</AnswerTable>
                      ) : (
                        <FieldValue muted>
                          No questions in this section.
                        </FieldValue>
                      )}
                    </SectionCard>
                  );
                })
              ) : (
                <CardLine>No sections in this form.</CardLine>
              )}
            </ExpansionPanel>
          </Box>
        ))
      ) : (
        <CardLine>This application has no answers in any form.</CardLine>
      )}
    </Box>
  );
};
